import { useObservable } from '@streem/sdk-react';
import {
    ChangeEvent,
    Context,
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
} from 'react';
import { binarySearch } from '../util/binary_search';
import {
    convertTranscriptionSegmentsToBlocks,
    TranscriptBlock,
} from '../util/convert_transcription_segments_to_blocks';
import { sortArrayByStartTime } from '../util/sort_array_by_start_time';
import { useDetailSession } from './detail_session_hooks';

export type IndexChangedCallback = (index: number) => void;
export interface TranscriptionContext {
    transcriptBlocks: TranscriptBlock[];
    registerIndexChangedCallback: (cb: IndexChangedCallback) => void;
    unregisterIndexChangedCallback: (cb: IndexChangedCallback) => void;
    timeUpdateHandler: (event: Event) => void;
    autoScrollModeActive: boolean;
    updateAutoScrollMode: (mode: boolean) => void;
    handleTimeStampJump: (timeInMs: number) => void;
    initializeVideoElementRef: (element: HTMLVideoElement) => void;
    handleSearch: (e: ChangeEvent<HTMLInputElement>) => void;
    searchValue: string;
    clearSearch: () => void;
}

const transcriptonContext: Context<TranscriptionContext | undefined> = createContext(undefined);

export function useInitializeTranscriptionContextProvider(): TranscriptionContext {
    const detailSession = useDetailSession();
    const [users = []] = useObservable(detailSession.users.users);
    const userLength = users.length;
    const [searchValue, setSearchValue] = useState('');

    const [transcriptionSegments = []] = useObservable(
        detailSession.transcriptionController.transcriptionSegments,
    );
    const transcriptionBlocks = useMemo(() => {
        // we always expect at least 1 user
        if (userLength === 0) {
            return [];
        }
        const blocks = convertTranscriptionSegmentsToBlocks(transcriptionSegments, users);
        return sortArrayByStartTime(blocks);
        // Note: userLength was added because updates to users was not always triggering
        //       this to update.  (Unfortunately, this will update multiple times,
        //       because there is no current mechanism for knowing when the userController
        //       has completed its initial load.  If this turns out to be a performance
        //       issue, we may want to pull the user info via API call instead of UserController)
    }, [transcriptionSegments, users, userLength]);
    const indexChangedCallbacks = useRef<Set<IndexChangedCallback>>(new Set());
    const registerIndexChangedCallback = useRef((cb: IndexChangedCallback) =>
        indexChangedCallbacks.current.add(cb),
    );
    const unregisterIndexChangedCallback = useRef((cb: IndexChangedCallback) =>
        indexChangedCallbacks.current.delete(cb),
    );

    const videoElementRef = useRef<HTMLVideoElement>();
    const [autoScrollModeActive, setAutoScrollModeActive] = useState(true);

    const initializeVideoElementRef = useCallback((videoElement: HTMLVideoElement) => {
        videoElementRef.current = videoElement;
    }, []);

    const handleSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        if (videoElementRef.current) {
            videoElementRef.current.pause();
        }
        const query = e.target.value;
        setSearchValue(query);
    }, []);

    const clearSearch = useCallback(() => {
        setSearchValue('');
    }, []);

    const handleTimeStampJump = useCallback((timeInMs: number) => {
        const clamped = Math.max(0, Math.floor(timeInMs));
        const totalSeconds = Math.floor(clamped / 1000);

        if (videoElementRef.current) {
            videoElementRef.current.currentTime = totalSeconds;
        }
    }, []);

    const transcriptionBlocksFlooredStartTime = useMemo(() => {
        return transcriptionBlocks.map(it => ({
            ...it,
            // The HTML Video element reports time in seconds with no fractions, while our transcript items report times in ms with fractions
            // this helps fix edge cases where the video element time in seconds causes binary search to select the wrong item b/c the fraction was closer to another item
            // instead of comparing ints to floats, we compare ints to ints
            // Examples: 26955 -> 26000, 29915 -> 29000, 30795 -> 30000
            startTimeMs: Math.floor(it.startTimeMs / 1000) * 1000,
        }));
    }, [transcriptionBlocks]);
    const timeUpdateHandler = useCallback(
        (event: Event) => {
            const videoElement = event.currentTarget as HTMLVideoElement | null;
            if (!videoElement) return;
            const currentTimeS = videoElement.currentTime ?? 0;
            const matchingIdx =
                binarySearch(transcriptionBlocksFlooredStartTime, currentTimeS * 1000) ?? 0;
            indexChangedCallbacks.current.forEach(cb => cb(matchingIdx));
        },
        [transcriptionBlocksFlooredStartTime],
    );

    return useMemo(
        () => ({
            transcriptBlocks: transcriptionBlocks,
            registerIndexChangedCallback: registerIndexChangedCallback.current,
            unregisterIndexChangedCallback: unregisterIndexChangedCallback.current,
            timeUpdateHandler: timeUpdateHandler,
            autoScrollModeActive,
            updateAutoScrollMode: setAutoScrollModeActive,
            handleTimeStampJump,
            initializeVideoElementRef,
            handleSearch,
            searchValue,
            clearSearch,
        }),
        [
            transcriptionBlocks,
            timeUpdateHandler,
            autoScrollModeActive,
            handleTimeStampJump,
            initializeVideoElementRef,
            handleSearch,
            searchValue,
            clearSearch,
        ],
    );
}

export function useTranscriptionContext(): TranscriptionContext {
    const context = useContext(transcriptonContext);
    if (context == null) {
        throw new Error(
            'Unable to useTranscriptionBlocks without parent <TranscriptionBlocksProvider />',
        );
    }
    return context;
}

export const TranscriptionContextProvider = transcriptonContext.Provider;
